/* eslint-disable @next/next/no-img-element */
import React from 'react'
import styled from '@emotion/styled'
import { gsColors } from '../../../../styles/Variables'
import { useAllMarketServices } from '../../../../services/GraphQlClientService'
import { mq, cartPricing, formatCurrency, totalServiceInstances, estimateTaxesAndFees } from '../../../../helpers'

const SelectedServicesDiv = styled.div`
  width: 100%;
  background-color: ${gsColors.white};

  .cart-header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${gsColors.white};
    padding: 19px 52px;

    .cart-icon {
      width: 21px;
      height: 18px;
      margin-right: 10px;
    }

    .title {
      font-family: Euclid;
      font-size: 16px;
      font-weight: 600;
      text-transform: capitalize;
      color: ${gsColors.sunsetOrange};
    }

    .close {
      all: unset;
      position: absolute;
      right: 20px;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      cursor: pointer;

      img {
        width: 18px;
        height: 18px;
      }
    }

    ${mq('min', '768px')} {
      justify-content: flex-start;
      padding: 20px 38px;

      .cart-icon {
        display: none;
      }

      .title {
        color: ${gsColors.black};
        font-size: 18px;
      }
    }
  }

  .services {
    max-height: 300px;
    overflow: auto;
    width: 100%;
    margin: 0;
    padding: 0;

    li {
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 10px;
      background-color: ${gsColors.eggshell};
      border-bottom: 1px solid ${gsColors.sand};
      padding: 20px 16px;

      ${mq('min', '768px')} {
        gap: 12px;
        padding: 20px 38px;
      }

      .row {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 16px;
      }

      .service-name {
        font-family: Euclid;
        font-size: 16px;
        font-weight: 600;
        color: ${gsColors.black};
        text-transform: capitalize;

        .count {
          font-weight: 400;
        }

        ${mq('min', '768px')} {
          font-size: 18px;
        }
      }

      .add-on {
        font-family: Euclid;
        font-size: 14px;
        font-weight: 400;
        color: ${gsColors.darkGrey};
        text-transform: capitalize;

        ${mq('min', '768px')} {
          font-size: 18px;
        }
      }

      .price {
        font-family: Euclid;
        font-size: 14px;
        font-weight: 400;
        color: ${gsColors.darkGrey};
        white-space: nowrap;
        text-align: right;

        ${mq('min', '768px')} {
          font-size: 18px;
        }
      }

      .remove {
        all: unset;
        cursor: pointer;
        width: fit-content;
        font-family: Euclid;
        font-size: 14px;
        font-weight: 400;
        text-decoration: underline;
        color: ${gsColors.darkGrey};

        ${mq('min', '768px')} {
          font-size: 16px;
        }
      }
    }
  }

  .add-service {
    all: unset;
    box-sizing: border-box;
    display: block;
    width: 100%;
    cursor: pointer;
    background-color: ${gsColors.eggshell};
    color: ${gsColors.sunsetOrange};
    font-family: Euclid;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 1.28px;
    text-transform: capitalize;
    padding: 20px 16px;

    ${mq('min', '768px')} {
      font-size: 18px;
      letter-spacing: 0.9px;
      padding: 20px 38px;
    }
  }

  .subtotal {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    background-color: ${gsColors.white};
    font-family: Euclid;
    font-size: 16px;
    color: ${gsColors.black};
    padding: 24px 16px 8px;

    .subtotal-label {
      font-weight: 600;
    }

    .subtotal-amount {
      font-weight: 400;
    }

    ${mq('min', '768px')} {
      font-size: 18px;
      padding: 20px 38px;
    }
  }
`

const SelectedServices = ({
  step,
  services,
  deselectMarketService,
  goToStep2,
  market = null,
  onClose,
}) => {
  const allMarketServices = useAllMarketServices(market?.id)
  const { lines, subtotal } = cartPricing(allMarketServices, services, market?.id)
  const { total, hasRates } = estimateTaxesAndFees(subtotal, market)

  // The cart renders the market's client-facing display price throughout (base for
  // 'Base' markets, fee/tip-inclusive otherwise) with no fee notes. The subtotal is
  // fee-inclusive for non-'Base' markets and the plain services total for 'Base'
  // markets (where fees are not surfaced to the client).
  const showDisplayPrice = market?.priceDisplay?.toLowerCase() !== 'base'
  const showTaxInclusiveTotal = showDisplayPrice && hasRates && subtotal > 0

  /**
   * Remove is always offered, including for the last service in the cart —
   * previously it was hidden on the availability search step, which read as a
   * missing link. Emptying the cart there sends the user back to service
   * selection (handled by deselectMarketService), so close the drawer with it.
   */
  const removeService = serviceId => {
    const isLastService = services.length <= 1

    deselectMarketService(serviceId)

    if (isLastService && step === 3) {
      onClose?.()
    }
  }

  return (
    <SelectedServicesDiv>
      <div className="cart-header">
        <img className="cart-icon" src="/images/book-calendar-icon.svg" alt="" aria-hidden="true" />
        <span className="title">My Services ({totalServiceInstances(services)})</span>
        {onClose && (
          <button className="close" id="cart-close" onClick={onClose} aria-label="Close">
            <img src="/images/cart-close-icon.svg" alt="" aria-hidden="true" />
          </button>
        )}
      </div>

      <ul className="services" id="cart-list">
        {lines.map(line => {
          if (!line.name) {
            return null
          }

          return (
            <li key={line.serviceId}>
              <div className="row">
                <span className="service-name">
                  {line.name.toLowerCase()}
                  {line.instances > 1 && <span className="count"> ({line.instances})</span>}
                </span>
                <span className="price">{formatCurrency(line.displayLineTotal)}</span>
              </div>

              {line.addOns.map(addOn => (
                <div className="row" key={addOn.name}>
                  <span className="add-on">
                    + {addOn.name}{line.instances > 1 ? ` (${line.instances})` : ''}
                  </span>
                  <span className="price">{formatCurrency(addOn.displayLineTotal)}</span>
                </div>
              ))}

              <button
                className="remove"
                data-cart-remove
                onClick={() => removeService(line.serviceId)}
              >
                Remove
              </button>
            </li>
          )
        })}
      </ul>

      <button onClick={goToStep2} className="add-service">
        + Add Service
      </button>

      {lines.some(line => line.name) && (
        <div className="subtotal">
          <span className="subtotal-label">Subtotal</span>
          <span className="subtotal-amount">
            {formatCurrency(showTaxInclusiveTotal ? total : subtotal)}
          </span>
        </div>
      )}
    </SelectedServicesDiv>
  )
}

export default SelectedServices
